import type { BadgeProps } from '@/components/ui/badge';
import { USER_STATUS, enumLabel } from '@/lib/enums';

/**
 * Display formatters + small view helpers for the Users feature.
 * Ported verbatim from UserManagementDetailsScreen.kt:74-106 (formatGender / formatKYCStatus / formatDevice).
 */

/** M -> Male, F -> Female, missing -> Not Set, anything else -> Unknown (parity formatGender). */
export function formatGender(gender: string | null | undefined): string {
  if (gender == null || gender === '') return 'Not Set';
  switch (gender.toUpperCase()) {
    case 'M':
      return 'Male';
    case 'F':
      return 'Female';
    default:
      return 'Unknown';
  }
}

/** VR -> Verified, anything else -> Not Verified (parity formatKYCStatus). */
export function formatKycStatus(verification: string | null | undefined): string {
  return verification?.toUpperCase() === 'VR' ? 'Verified' : 'Not Verified';
}

/** `deviceName + osName` joined by " + ", else em dash (parity formatDevice). */
export function formatDevice(
  deviceName: string | null | undefined,
  osName: string | null | undefined,
): string {
  const parts = [deviceName, osName].filter((p): p is string => !!p && p.trim() !== '');
  return parts.length > 0 ? parts.join(' + ') : '—';
}

/** ISO-8601 -> locale date string, safe on empty/invalid input. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export interface StatusDisplay {
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
}

/**
 * Map a backend user-status code to a badge label + variant.
 * Codes (UserStatus): ACT=Active, INA=Inactive, TER=Terminated — change-status #3 sends TER for ban.
 * TM-3665: when {@code deactivatedAt} is set (delete-account grace), prefer "Deactivated" over
 * INA — registry sets user_status=INA on self-deactivate, which would otherwise look like Suspend.
 * Label comes from the shared enum registry so list + detail never diverge (except deactivated).
 */
export function userStatusDisplay(
  status: string | null | undefined,
  deactivatedAt?: string | null,
): StatusDisplay {
  if (deactivatedAt) {
    return { label: 'Deactivated', variant: 'secondary' };
  }
  const label = enumLabel(USER_STATUS, status, 'Unknown');
  switch (status?.toUpperCase()) {
    case 'ACT':
      return { label, variant: 'default' };
    case 'INA':
      return { label, variant: 'secondary' };
    case 'TER':
      return { label, variant: 'destructive' };
    default:
      return { label, variant: 'outline' };
  }
}

/** Ban = change-status with userStatus "TER" (doc 06 §3, note on #3). */
export const USER_STATUS_BANNED = 'TER';
